import React, { createContext, useCallback, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { THit } from '@leedpulse-monorepo/types';
import { IAdmin } from '@leedpulse-monorepo/interfaces';
import getAdminsByConstraints from '../../../../domain/use_cases/admins/getAdminsByConstraints';

export type AdminsController = {
  selected: IAdmin | null;
  enabledResults: IAdmin[];
  noSearchResults: boolean;
  status: ContextStatus;
  // Required actions (only what we need today).
  select: (admin: IAdmin) => void;
  fetchEnabled: () => Promise<void>;
  reset: () => void;
};

const AdminsContext = createContext<AdminsController | null>(null);

/**
 * Build an isolated "mini state" for the component that calls it.
 * This is intentionally NOT a global provider shared across the whole app.
 */
export const useAdminsController = (): AdminsController => {
  const [selected, setSelected] = useState<IAdmin | null>(null);
  const [enabledResults, setEnabledResults] = useState([] as IAdmin[]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [status, setStatus] = useState<ContextStatus>(ContextStatus.IDLE);

  const select = useCallback((admin: IAdmin) => {
    setSelected(admin);
  }, []);

  const fetchEnabled = useCallback(async () => {
    try {
      const useCaseResponse = await getAdminsByConstraints([{ field: 'id', op: 'orderBy', value: 'asc' }]);
      setEnabledResults(useCaseResponse);
      setStatus(ContextStatus.SUCCESS);
    } catch (error) {
      setStatus(ContextStatus.ERROR);
    }
  }, []);

  const reset = useCallback(() => {
    setSelected(null);
    setNoSearchResults(false);
    setStatus(ContextStatus.IDLE);
  }, []);

  return {
    selected,
    enabledResults,
    noSearchResults,
    status,
    select,
    fetchEnabled,
    reset,
  };
};

export const AdminsLocalProvider: React.FC<{
  controller: AdminsController;
  children: React.ReactNode;
}> = ({ controller, children }) => {
  return (
    <AdminsContext.Provider value={controller}>
      {children}
    </AdminsContext.Provider>
  );
};

export const useAdmins = (): AdminsController => {
  const controller = useContext(AdminsContext);
  if (!controller) {
    throw new Error('useAdmins must be used within AdminsLocalProvider');
  }
  return controller;
};

export default AdminsContext;
